/** Pricing rules, ported from the measurement tool so that the estimate and the
 *  measurement can never disagree. Each rule below exists because getting it
 *  wrong measurably distorted a published figure. */
import type { ModelPrice, TokenBundle } from './types'

/**
 * Price of one cache-WRITE token.
 *
 * A cache write is an input token that gets processed, so it can never cost less
 * than the prompt price. Two ways to get this wrong, both observed in the wild:
 *
 * - No published price does not mean free. Charging zero understates
 *   deepseek-v4.1-flash by ~37%, and that is the model whose low price is the
 *   product's headline argument.
 * - A published price BELOW the prompt price is a storage fee, not a processing
 *   fee. Taking gemini-3.5-flash's 0.083 $/M at face value against its 1.50 $/M
 *   prompt understated it by 13.6%.
 */
export function cacheWritePrice(model: ModelPrice): number {
  return Math.max(model.cache_write ?? 0, model.prompt)
}

export interface CostOptions {
  /** Use the measured 5-minute / 1-hour split of cache writes. On by default:
   *  80% of measured writes are 1-hour ones, so pretending otherwise understates
   *  an Anthropic bill by ~7.5%. */
  tieredCache?: boolean
}

/** Cost in USD of one run described by `usage`, on `model`. */
export function costOf(
  usage: TokenBundle,
  model: ModelPrice,
  options: CostOptions = {},
): number {
  const tiered = options.tieredCache ?? true
  const write = cacheWritePrice(model)

  // The 2x-prompt premium for a 1-hour TTL is an ANTHROPIC pricing rule. Applying
  // it to every provider that happens to publish a cache-write price invented
  // hundreds of dollars of cost in the first version of the extraction.
  const anthropicTiering = tiered && model.provider === 'anthropic' && model.cache_write !== null

  let cacheWriteCost: number
  if (anthropicTiering) {
    const untagged = Math.max(0, usage.cw - usage.cw_5m - usage.cw_1h)
    cacheWriteCost =
      usage.cw_5m * write + usage.cw_1h * 2 * model.prompt + untagged * write
  } else {
    cacheWriteCost = usage.cw * write
  }

  return (
    usage.in * model.prompt +
    usage.out * model.completion +
    usage.cr * model.cache_read +
    cacheWriteCost
  )
}

/**
 * Counterfactual: the same token flow on a model with no caching at all.
 *
 * This is the sensitivity band the cross-model comparison needs. The whole
 * measured corpus ran under aggressive caching, and without it the same work
 * costs about eight times more — enough to change which model is cheapest.
 */
export function costWithoutCache(usage: TokenBundle, model: ModelPrice): number {
  const totalInput = usage.in + usage.cw + usage.cr
  return totalInput * model.prompt + usage.out * model.completion
}

/** Sum of two bundles, used to fold delegated work into its parent request. */
export function addBundles(a: TokenBundle, b: TokenBundle): TokenBundle {
  return {
    in: a.in + b.in,
    out: a.out + b.out,
    cw: a.cw + b.cw,
    cr: a.cr + b.cr,
    cw_5m: a.cw_5m + b.cw_5m,
    cw_1h: a.cw_1h + b.cw_1h,
    turns: a.turns + b.turns,
  }
}

/** Scale a bundle, for adjusting the expected number of round trips.
 *
 *  Scaling follows the measured cost law `setup + n x marginal`, not a plain
 *  multiplication: the first call builds the cache and costs about four ordinary
 *  turns, so doubling the turns does NOT double the cost.
 */
export function scaleToTurns(
  usage: TokenBundle,
  targetTurns: number,
  setupShare: number,
): TokenBundle {
  if (usage.turns <= 0 || targetTurns <= 0) return usage
  const share = Math.min(0.95, Math.max(0, setupShare))
  const ratio = share + (1 - share) * (targetTurns / usage.turns)
  const scale = Math.max(0, ratio)
  return {
    // The setup cost lives in the cache WRITE of the first call, so the write
    // barely moves with the number of turns while the reads scale with it.
    in: Math.round(usage.in * scale),
    out: Math.round(usage.out * scale),
    cw: usage.cw,
    cr: Math.round(usage.cr * scale),
    cw_5m: usage.cw_5m,
    cw_1h: usage.cw_1h,
    turns: targetTurns,
  }
}
